import { useToast } from "vue-toastification"
import { useStore } from "vuex"
import passConstants from "@/constants/passConstants"
import { announce } from "@/composables/useLiveAnnouncer"

/* Extract plain text from a toast message (string, or component options object). */
const toastText = (message) => {
  if (typeof message === "string") return message
  if (message && typeof message === "object") {
    return message.props?.message || message.message || ""
  }
  return ""
}

/**
 * Composable that wraps vue-toastification's useToast
 * with feature flag support to disable toasts for specific schools.
 * For use in Vue component setup() functions.
 *
 * @returns {Object} Toast methods that respect the feature flag
 */
export default function useAppToast() {
  const toast = useToast()
  const store = useStore()

  const isToastDisabled = () => {
    const disabledSchoolIds = passConstants.TOAST_DISABLED_SCHOOL_IDS
    if (!disabledSchoolIds) {
      return false
    }

    const activeSchool = store?.getters?.["schools/activeSchool"]
    const schoolId = activeSchool?.id

    if (!schoolId) {
      return false
    }

    const disabledIds = disabledSchoolIds
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean)

    return disabledIds.includes(String(schoolId))
  }

  const success = (message, options) => {
    if (isToastDisabled()) {
      return
    }
    announce(toastText(message))
    return toast.success(message, options)
  }

  const error = (message, options) => {
    if (isToastDisabled()) {
      return
    }
    announce(toastText(message), { assertive: true })
    return toast.error(message, options)
  }

  const info = (message, options) => {
    if (isToastDisabled()) {
      return
    }
    announce(toastText(message))
    return toast.info(message, options)
  }

  const warning = (message, options) => {
    if (isToastDisabled()) {
      return
    }
    announce(toastText(message), { assertive: true })
    return toast.warning(message, options)
  }

  return {
    success,
    error,
    info,
    warning
  }
}
